import React, { useMemo } from "react";
import {
  convertAmountLocal,
  dateFormatter,
} from "../../../../global-functions/globalFunctions";
import { Avatar } from "../../../../components/Avatar/Avatar";
import InventoryChargeSign from "../../components/InventoryChargeSign";
import InventoryRoundOffValue from "../../components/InventoryRoundOffValue";
import InventorySignedRupeeAmount from "../../components/InventorySignedRupeeAmount";
import { getPayableWithRoundOff } from "../../Order/orderCalculations";
import "./InvoiceDetailsBody.css";

const InvoiceDetailsBody = ({
  invoiceDetails,
  invoiceSettingDetails,
  allTransaction,
}) => {
  const order = invoiceDetails?.orderId;
  const isPaid = invoiceDetails?.paymentStatus === "paid";

  const calculatePercentageAmount = (charge) => {
    const subTotal = order?.subTotal || 0;
    if (charge?.chargeId?.chargeType === "percentage") {
      const percentageValue = charge?.amount || 0;
      return (subTotal * percentageValue) / 100;
    }
    return 0;
  };

  const getCustomizationItem = (productId) =>
    order?.customization?.filter(
      (inventory) => inventory?.productId === productId
    ) || [];

  const calculateCustomizeTotal = (productId) => {
    return getCustomizationItem(productId).reduce((accumulator, dt) => {
      const value = parseFloat(dt?.price);
      return accumulator + (Number.isFinite(value) ? value : 0);
    }, 0);
  };

  const roundOff = useMemo(() => {
    const chargesForCalculation = order?.charges?.map((chrg) => ({
      value: chrg?.amount,
      chargeType: chrg?.chargeId?.chargeType,
      add: chrg?.chargeId?.add,
    }));

    return getPayableWithRoundOff(order?.subTotal, chargesForCalculation).roundOff;
  }, [order?.subTotal, order?.charges]);

  const createdByName =
    order?.actionBy?.name || order?.createdBy?.name;
  const createdByImage =
    order?.actionBy?.image || order?.createdBy?.image;

  const formatLineAmount = (value) =>
    Number(parseFloat(value) || 0).toFixed(2);

  return (
    <div className="invoice-details-page simple-scrollbar">
      <div className="invoice-details-card">
        <div className="invoice-details-header">
          <div className="invoice-business">
            {invoiceSettingDetails?.logo && (
              <img
                src={invoiceSettingDetails.logo}
                alt={invoiceSettingDetails?.businessName || "Business logo"}
                className="mw-175p mh-125p object-fit-contain"
              />
            )}
            <div className="invoice-business-name text-capitalize">
              {invoiceSettingDetails?.businessName}
            </div>
            {invoiceSettingDetails?.businessEmail && (
              <div className="invoice-business-contact">
                {invoiceSettingDetails.businessEmail}
              </div>
            )}
            {invoiceSettingDetails?.businessPhone && (
              <div className="invoice-business-contact text-capitalize">
                {invoiceSettingDetails.businessPhone}
              </div>
            )}
            {invoiceSettingDetails?.fullAddress && (
              <div className="invoice-business-contact">
                {invoiceSettingDetails.fullAddress}
              </div>
            )}
          </div>

          <div className="invoice-meta-panel">
            <div className="invoice-title">INVOICE</div>
            <div className="invoice-number">#{invoiceDetails?.invoiceNumber}</div>
            <div className="invoice-meta-grid">
              {invoiceSettingDetails?.businessGSTNumber && (
                <div className="invoice-meta-row">
                  <span className="invoice-meta-label">GST</span>
                  <span className="invoice-meta-value">
                    {invoiceSettingDetails.businessGSTNumber}
                  </span>
                </div>
              )}
              <div className="invoice-meta-row">
                <span className="invoice-meta-label">Date</span>
                <span className="invoice-meta-value">
                  {dateFormatter(invoiceDetails?.createdAt)}
                </span>
              </div>
              <div className="invoice-meta-row">
                <span className="invoice-meta-label">Status</span>
                <span
                  className={`invoice-status-badge ${
                    isPaid
                      ? "invoice-status-badge--paid"
                      : "invoice-status-badge--unpaid"
                  }`}
                >
                  {invoiceDetails?.paymentStatus || invoiceDetails?.orderStatus}
                </span>
              </div>
              {invoiceDetails?.paymentType && (
                <div className="invoice-meta-row">
                  <span className="invoice-meta-label">Payment</span>
                  <span className="invoice-meta-value text-capitalize">
                    {invoiceDetails.paymentType}
                  </span>
                </div>
              )}
              {invoiceDetails?.referenceNumber && (
                <div className="invoice-meta-row">
                  <span className="invoice-meta-label">Reference</span>
                  <span className="invoice-meta-value">
                    {invoiceDetails.referenceNumber}
                  </span>
                </div>
              )}
              {createdByName && (
                <div className="invoice-meta-row">
                  <span className="invoice-meta-label">Created by</span>
                  <span className="invoice-meta-value invoice-created-by">
                    <Avatar
                      name={createdByName}
                      count={1}
                      image={createdByImage}
                      size="xxs"
                      color="primary"
                    />
                    {createdByName}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {order?.status === "returned" && (
          <div className="invoice-return-banner">
            <strong>Return reason:</strong> {order?.reason || "—"}
          </div>
        )}

        <div className="invoice-customer-card">
          <div>
            <div className="invoice-customer-label">Customer</div>
            <div className="invoice-customer-value">
              {order?.buyer?.name || "Guest"}
            </div>
          </div>
          {order?.buyer?.phone && (
            <div>
              <div className="invoice-customer-label">Phone</div>
              <div className="invoice-customer-value">{order.buyer.phone}</div>
            </div>
          )}
          {order?.buyer?.email && (
            <div>
              <div className="invoice-customer-label">Email</div>
              <div className="invoice-customer-value">{order.buyer.email}</div>
            </div>
          )}
          {order?.tableId?.tableName && (
            <div>
              <div className="invoice-customer-label">Table</div>
              <div className="invoice-customer-value">
                {order.tableId.tableName}
              </div>
            </div>
          )}
          {order?.typeOfCustomer && (
            <div>
              <div className="invoice-customer-label">Order type</div>
              <div className="invoice-customer-value text-capitalize">
                {order.typeOfCustomer}
              </div>
            </div>
          )}
        </div>

        <div className="invoice-items-wrap">
          <table className="table invoice-items-table w-100 mb-0">
            <thead>
              <tr>
                <th className="text-center" style={{ width: "48px" }}>
                  #
                </th>
                <th className="text-start">Item</th>
                <th className="text-center">Code</th>
                <th className="text-center">Qty</th>
                <th className="text-end">Rate</th>
                <th className="text-end">Amount</th>
              </tr>
            </thead>
            <tbody>
              {order?.products?.map((data, index) => {
                const lineBase =
                  (parseFloat(data?.productId?.sellingPrice) || 0) *
                  (data?.quantity || 0);
                const addons = getCustomizationItem(data?.productId?._id);
                const lineTotal = lineBase + calculateCustomizeTotal(data?.productId?._id);

                return (
                  <React.Fragment key={data?._id || data?.productId?._id || index}>
                    <tr className="invoice-item-row text-center">
                      <td>{index + 1}</td>
                      <td className="text-start fw-medium text-capitalize">
                        {data?.productId?.productName}
                      </td>
                      <td>{data?.productId?.productCode || "—"}</td>
                      <td>{data?.quantity}</td>
                      <td className="text-end">
                        ₹ {formatLineAmount(data?.productId?.sellingPrice)}
                      </td>
                      <td className="text-end fw-medium">
                        ₹ {formatLineAmount(lineBase)}
                      </td>
                    </tr>

                    {addons.map((material, materialIndex) => (
                      <tr
                        key={`${data?.productId?._id}-addon-${materialIndex}`}
                        className="invoice-addon-row"
                      >
                        <td colSpan={6}>
                          <div className="d-flex align-items-center justify-content-end gap-3 pe-2">
                            <span className="invoice-addon-text text-capitalize">
                              + {material?.quantity} {material?.unit?.name} ·{" "}
                              {material?.inventoryItem?.name}
                            </span>
                            <InventorySignedRupeeAmount
                              isAdd={true}
                              amount={material?.price}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}

                    {addons.length > 0 && (
                      <tr className="invoice-line-total-row">
                        <td colSpan={5} className="text-end">
                          Line total (incl. add-ons)
                        </td>
                        <td className="text-end">
                          ₹ {formatLineAmount(lineTotal)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="invoice-footer">
          <div>
            {allTransaction?.length > 0 ? (
              <div className="invoice-transactions-card">
                <h6 className="invoice-transactions-title">Payment history</h6>
                <table className="table invoice-transactions-table w-100">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Amount</th>
                      <th>Mode</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allTransaction.map((txn, index) => (
                      <tr key={txn?._id || index}>
                        <td>{index + 1}</td>
                        <td className="fw-medium">
                          {convertAmountLocal(txn?.amountReceived)}
                        </td>
                        <td className="text-capitalize">{txn?.paymentMode}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="invoice-transactions-card">
                <h6 className="invoice-transactions-title">Payment history</h6>
                <p className="invoice-transactions-empty mb-0">
                  No transactions recorded for this order.
                </p>
              </div>
            )}
          </div>

          <div className="invoice-summary-card">
            <div className="invoice-summary-title">Amount summary</div>

            <div className="invoice-summary-row">
              <span className="invoice-summary-label">Subtotal</span>
              <span className="invoice-summary-value">
                ₹ {order?.subTotal?.toFixed(2)}
              </span>
            </div>

            {order?.charges?.map((chrg) => {
              const chargeAmount =
                chrg?.chargeId?.chargeType === "amount"
                  ? chrg?.amount
                  : calculatePercentageAmount(chrg);

              return (
                <div className="invoice-summary-row" key={chrg?._id}>
                  <span className="invoice-summary-label text-capitalize">
                    (<InventoryChargeSign isAdd={chrg?.chargeId?.add} />){" "}
                    {chrg?.chargeId?.chargeName}
                    {chrg?.chargeId?.chargeType === "percentage" && (
                      <span className="text-muted ms-1">({chrg?.amount}%)</span>
                    )}
                  </span>
                  <span className="invoice-summary-value">
                    <InventorySignedRupeeAmount
                      isAdd={chrg?.chargeId?.add}
                      amount={chargeAmount}
                    />
                  </span>
                </div>
              );
            })}

            <div className="invoice-summary-row">
              <span className="invoice-summary-label">Round off</span>
              <span className="invoice-summary-value">
                <InventoryRoundOffValue roundOff={roundOff} />
              </span>
            </div>

            <div className="invoice-summary-row invoice-summary-row--total">
              <span className="invoice-summary-total-label">Total</span>
              <span className="invoice-summary-total-value">
                ₹ {order?.amount?.toFixed(2)}
              </span>
            </div>

            {invoiceDetails?.receivedAmount != null &&
              invoiceDetails.receivedAmount !== order?.amount && (
                <div className="invoice-summary-row mt-2 pt-2 border-top">
                  <span className="invoice-summary-label">Received</span>
                  <span className="invoice-summary-value text-success">
                    ₹ {invoiceDetails.receivedAmount.toFixed(2)}
                  </span>
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetailsBody;
